import { CommonModule } from '@angular/common'
import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core'
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { NbButtonModule, NbCardModule, NbFormFieldModule, NbIconModule, NbInputModule, NbSelectModule } from '@nebular/theme'
import { Subject } from 'rxjs'
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators'
import { EmailTheme } from '../../interfaces/email-theme.interface'
import { EmailComposerStateService } from '../../services/email-composer-state.service'
import { EmailComposerService } from '../../services/email-composer.service'

@Component({
  selector: 'ngx-theme-manager',
  templateUrl: './theme-manager.component.html',
  styleUrls: ['./theme-manager.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, NbCardModule, NbButtonModule, NbFormFieldModule, NbInputModule, NbSelectModule, NbIconModule]
})
export class ThemeManagerComponent implements OnInit, OnDestroy {
  @Output() editTheme = new EventEmitter<number>()
  @Output() createTheme = new EventEmitter<void>()

  themes: EmailTheme[] = []
  loading = false

  searchControl = new FormControl('')
  activeFilterControl = new FormControl(true)
  systemThemeControl = new FormControl('')

  private destroy$ = new Subject<void>()
  private currentSearch = ''

  constructor(private emailComposerService: EmailComposerService, private state: EmailComposerStateService) {}

  ngOnInit(): void {
    this.loadThemes()
    this.setupSearch()
    // React to global search (debounced) and keep latest value
    this.state.search$.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((value) => {
      this.currentSearch = value || ''
      this.loadThemes()
    })
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  setupSearch(): void {
    this.searchControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe(() => this.loadThemes())

    this.activeFilterControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.loadThemes())

    this.systemThemeControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.loadThemes())
  }

  loadThemes(): void {
    this.loading = true

    const search = this.searchControl.value || this.currentSearch || '' || undefined
    const is_active = this.activeFilterControl.value !== null ? this.activeFilterControl.value : undefined
    const is_system_theme = this.systemThemeControl.value === '' ? undefined : this.systemThemeControl.value === 'true'

    this.emailComposerService
      .getThemes(search, is_active, is_system_theme)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.themes = response.data
          this.loading = false
        },
        error: (error) => {
          console.error('Error loading themes:', error)
          this.loading = false
        }
      })
  }

  onCreateTheme(): void {
    this.createTheme.emit()
  }

  onEditTheme(theme: EmailTheme): void {
    if (theme.id) {
      this.editTheme.emit(theme.id)
    }
  }

  onDeleteTheme(theme: EmailTheme): void {
    if (theme.id && confirm(`Are you sure you want to delete theme "${theme.name}"?`)) {
      this.emailComposerService
        .deleteTheme(theme.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            if (response.success) {
              this.loadThemes()
            } else {
              console.error('Error deleting theme:', response.message)
            }
          },
          error: (error) => {
            console.error('Error deleting theme:', error)
            // Handle specific error messages from API
            if (error.error?.message) {
              alert(error.error.message)
            }
          }
        })
    }
  }

  onSetDefault(theme: EmailTheme): void {
    if (theme.id) {
      // Optimistic update
      const previous = this.themes.map((t) => ({ id: t.id, is_default: t.is_default }))
      this.themes = this.themes.map((t) => ({
        ...t,
        is_default: t.id === theme.id
      }))

      this.emailComposerService
        .setDefaultTheme(theme.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            if (response.success) {
              // Ensure alignment with server response
              const newDefaultId = response.data.id
              this.themes = this.themes.map((t) => ({
                ...t,
                is_default: t.id === newDefaultId
              }))
              // Propagate selection to the rest of the app
              this.emailComposerService.setSelectedTheme(response.data)
            }
          },
          error: (error) => {
            console.error('Error setting default theme:', error)
            // Revert optimistic update on error
            this.themes = this.themes.map((t) => {
              const prev = previous.find((p) => p.id === t.id)
              return prev ? { ...t, is_default: prev.is_default } : t
            })
          }
        })
    }
  }

  getThemePreview(theme: EmailTheme): string {
    // Generate a simple preview based on theme colors from global_css
    if (theme.global_css) {
      const primary = theme.global_css['primary_color'] || theme.global_css['primaryColor'] || '#667eea'
      const secondary = theme.global_css['secondary_color'] || theme.global_css['secondaryColor'] || '#764ba2'
      return `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`
    }
    return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
  }

  getThemeColors(theme: EmailTheme): { name: string; value: string }[] {
    const colors: { name: string; value: string }[] = []

    if (theme.global_css) {
      Object.keys(theme.global_css).forEach((key) => {
        const value = theme.global_css![key]
        if (typeof value === 'string' && (value.startsWith('#') || value.startsWith('rgb') || value.startsWith('hsl'))) {
          colors.push({
            name: key
              .replace(/_/g, ' ')
              .replace(/([A-Z])/g, ' $1')
              .trim(),
            value: value
          })
        }
      })
    }

    return colors
  }

  getPrimaryColor(theme: EmailTheme): string {
    if (!theme.global_css) return '#3b82f6'
    return theme.global_css['primary_color'] || theme.global_css['primaryColor'] || theme.global_css['text_color'] || '#3b82f6'
  }

  getBackground(theme: EmailTheme): string {
    return theme.global_css?.['background_color'] || '#ffffff'
  }

  getTextColor(theme: EmailTheme): string {
    return theme.global_css?.['text_color'] || '#111827'
  }

  getFontFamily(theme: EmailTheme): string {
    return theme.global_css?.['font_family'] || 'Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif'
  }

  getCss(theme: EmailTheme, key: string, fallback: string): string {
    return theme.global_css?.[key] || fallback
  }
}
